import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Terminal } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n/LanguageContext";

const NAV_IDS = ["hero", "hizmetler", "teknolojiler", "projeler", "surec", "iletisim"];
const LANGS = ["tr", "en", "ar"];

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const LangSwitcher = ({ className = "" }) => (
    <div className={`flex items-center gap-1 rounded-full border border-white/10 p-1 ${className}`} data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase transition-colors duration-200 ${
            lang === l ? "bg-[#5B7FFF] text-[#15171C]" : "text-slate-400 hover:text-white"
          }`}
          data-testid={`lang-btn-${l}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "bg-[#15171C]/80" : "bg-[#15171C]/60"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          className="flex items-center gap-2 group"
          data-testid="header-logo"
        >
          <span className="w-8 h-8 rounded-md glow-badge flex items-center justify-center">
            <Terminal size={16} />
          </span>
          <span className="font-heading font-black text-xl tracking-tighter text-white">
            CDN BİLİŞİM<span className="text-[#5B7FFF]">.</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_IDS.map((id) => (
            <button
              key={id}
              onClick={() => go(id)}
              className="text-sm text-slate-400 hover:text-[#5B7FFF] transition-colors duration-200"
              data-testid={`nav-link-${id}`}
            >
              {t.nav[id]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitcher className="hidden md:flex" />
          <button
            onClick={() => go("iletisim")}
            className="hidden sm:inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold bg-[#5B7FFF] text-[#15171C] hover:shadow-[0_0_25px_rgba(91,127,255,0.5)] transition-shadow duration-300"
            data-testid="header-cta-teklif-al"
          >
            {t.cta_quote}
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-[#15171C]/95 backdrop-blur-xl border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_IDS.map((id) => (
                <button
                  key={id}
                  onClick={() => go(id)}
                  className="text-start py-3 text-slate-300 hover:text-[#5B7FFF] transition-colors border-b border-white/5"
                  data-testid={`mobile-nav-link-${id}`}
                >
                  {t.nav[id]}
                </button>
              ))}
              <div className="flex justify-center py-3">
                <LangSwitcher />
              </div>
              <button
                onClick={() => go("iletisim")}
                className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#5B7FFF] text-[#15171C]"
                data-testid="mobile-cta-teklif-al"
              >
                {t.cta_quote}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
